import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { Toast } from '../components/Toast';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function Login() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      if (data.user) {
        // Clean up old localStorage keys if they exist
        localStorage.removeItem('dietcrm_user');
        localStorage.removeItem('welma_user');

        // Navigate to dashboard (AuthContext will handle session)
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Incorrect email or password');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mx-auto mb-4">
            <img
              src="/welma-logo.svg"
              alt="Welma"
              className="h-14 w-auto max-w-[240px]"
            />
          </div>
          <p className="text-[#8A8277] text-lg font-light">Your daily practice assistant</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-10">
          <h2 className="text-2xl font-semibold text-[#3D3935] mb-8">
            Welcome back
          </h2>
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#6B5E52] mb-2">
                Email
              </label>
              <input type="email" value={email} onChange={e => {
              setEmail(e.target.value);
              setError('');
            }} placeholder="your.email@example.com" className={`w-full px-4 py-3.5 rounded-xl border ${error ? 'border-red-500' : 'border-gray-200'} focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all bg-gray-50/50`} inputMode="email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6B5E52] mb-2">
                Password
              </label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => {
                setPassword(e.target.value);
                setError('');
              }} placeholder="Enter your password" className={`w-full px-4 py-3.5 rounded-xl border ${error ? 'border-red-500' : 'border-gray-200'} focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all pr-12 bg-gray-50/50`} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </div>
            <button type="submit" disabled={isLoading} className={`w-full bg-[#5B8E87] text-white font-medium py-3.5 rounded-xl shadow-sm transition-all mt-6 ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#4F7A74] active:scale-[0.99]'}`}>
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <p className="text-xs text-[#9B9491] text-center mt-6">
            Need help logging in? Reach out to support.
          </p>
        </div>
      </div>
      {showToast && <Toast message="Incorrect email or password" onClose={() => setShowToast(false)} />}
    </div>;
}